import { useEffect, useState, useCallback } from "react";
import { FileArrowDown, DownloadSimple, Archive, ClipboardText } from "@phosphor-icons/react";
import api, { formatApiError, downloadBlob } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const TIER_BADGE = {
  light: "bg-sky-50 text-sky-700 border-sky-200",
  lightpro: "bg-indigo-50 text-indigo-700 border-indigo-200",
  shield: "bg-emerald-50 text-emerald-700 border-emerald-200",
  all: "bg-slate-100 text-slate-600 border-slate-300",
};
const TIER_LABEL = { light: "Retired Light Base", lightpro: "ClaroShield Light", shield: "ClaroShield", all: "All tiers" };

export default function TestScriptsPanel() {
  const [scripts, setScripts] = useState([]);
  const [pack, setPack] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [form, setForm] = useState({ entity: "", plan: "", tester: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/test-scripts");
      setScripts(data.scripts || []);
      setPack(data.pack || "");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load test scripts");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const download = async (item) => {
    setBusy(item.id);
    try {
      const p = new URLSearchParams({ id: item.id });
      if (form.entity) p.set("entity", form.entity);
      if (form.plan) p.set("plan", form.plan);
      if (form.tester) p.set("tester", form.tester);
      await downloadBlob(`/admin/test-scripts/download?${p.toString()}`, item.id);
    } catch (e) {
      toast.error(e.message || "Download failed");
    } finally { setBusy(""); }
  };

  const primary = scripts.filter((s) => s.group === "primary");
  const modules = scripts.filter((s) => s.group === "module");
  const packUrl = pack ? `${window.location.origin}/fat/${pack}` : "";

  const Row = ({ item }) => (
    <div className="flex items-center justify-between gap-3 border border-slate-100 px-3 py-2" data-testid={`test-script-row-${item.id}`}>
      <div className="min-w-0">
        <div className="text-sm font-medium text-slate-800 truncate">{item.title}</div>
        <div className="text-[11px] text-slate-400 font-mono truncate">{item.id}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className={`text-[10px] px-1.5 py-0.5 border rounded-sm ${TIER_BADGE[item.tier] || TIER_BADGE.all}`}>{TIER_LABEL[item.tier] || item.tier}</span>
        <Button size="sm" variant="outline" className="rounded-none h-8 text-xs" disabled={busy === item.id}
          onClick={() => download(item)} data-testid={`test-script-download-${item.id}`}>
          <DownloadSimple size={14} className="mr-1" />{busy === item.id ? "…" : "Download"}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="test-scripts-panel">
      <div className="pt-2">
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2"><ClipboardText size={20} weight="duotone" /> Test Scripts (FAT runbooks)</h2>
        <p className="text-sm text-slate-500 mt-1">Manual acceptance-test runbooks (.docx) with PASS/FAIL boxes, tester notes and screenshot space. Fill the form below to prefill each runbook's cover for a client-ready copy.</p>
      </div>

      <div className="border border-slate-200 bg-white p-4">
        <div className="text-xs font-semibold text-slate-600 mb-2">Prefill cover (optional)</div>
        <div className="grid gap-3 md:grid-cols-3">
          <div>
            <Label className="text-xs">Entity name</Label>
            <Input value={form.entity} onChange={(e) => setForm({ ...form, entity: e.target.value })}
              placeholder="Sunset Solar LLC" className="rounded-none mt-1" data-testid="test-script-entity" />
          </div>
          <div>
            <Label className="text-xs">Subscription tier</Label>
            <Input value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}
              placeholder="ClaroShield Light" className="rounded-none mt-1" data-testid="test-script-plan" />
          </div>
          <div>
            <Label className="text-xs">Tester name</Label>
            <Input value={form.tester} onChange={(e) => setForm({ ...form, tester: e.target.value })}
              placeholder="Jane Doe" className="rounded-none mt-1" data-testid="test-script-tester" />
          </div>
        </div>
        {packUrl && (
          <div className="mt-4 flex items-center gap-2">
            <Button variant="outline" className="rounded-none" onClick={() => window.open(packUrl, "_blank")} data-testid="test-scripts-download-pack">
              <Archive size={15} className="mr-1" /> Download all (.zip)
            </Button>
            <span className="text-[11px] text-slate-400">The zip is the un-prefilled set of every runbook.</span>
          </div>
        )}
      </div>

      {loading ? <div className="text-sm text-slate-500" data-testid="test-scripts-loading">Loading…</div> : (
        <>
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500 mb-2">
              <FileArrowDown size={14} /> Role, tier & metering runbooks
            </div>
            <div className="space-y-1.5" data-testid="test-scripts-primary">
              {primary.map((s) => <Row key={s.id} item={s} />)}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500 mb-2">
              <FileArrowDown size={14} /> Per-module runbooks ({modules.length})
            </div>
            <div className="space-y-1.5 max-h-96 overflow-y-auto pr-1" data-testid="test-scripts-modules">
              {modules.map((s) => <Row key={s.id} item={s} />)}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
